// BUILD 2 C2-13: complete human-readable locale catalogs; no placeholder locale suffixes.
import type { NamespaceCatalog } from '@/i18n/ns';

const keys = [
  'recognisedByVoice',
  'undoRecognition',
  'isSpeakerYou',
  'saveMyVoice',

  'soundsLike',
  'voiceMatch',
  'confirm',
  'notThisPerson',
  'sameVoiceAs',
  'bothSoundLike',
  'mergeAndName',
  'merge',
  'notSame',
  'checkingVoices',
  'voiceCheckFailed',
  'checkVoicesAgain',
  'reviewUncertain',
  'uncertainHint',
  'uncertainSpeaker',
  'keepAs',
  'moveTo',
  'someoneElse',
  'skip',
  'progress',
  'identificationOff',
  'turnOffVoiceSuggestions',
  'useMyVoice',
  'isThisYou',
  'promptTitle',
  'promptSetUp',
  'promptNoThanks',
  'consentLineVoice',
  'suggestNamesCheckbox',
  'predatesVoicePass',
  'errorUnavailable',
  'errorPassNotCurrent',
  'errorSuggestionGone',
] as const;
export type MeetingsVoiceCopyKey = (typeof keys)[number];

const en = {
  recognisedByVoice: 'Recognised by voice',
  undoRecognition: 'Undo',
  isSpeakerYou: 'Is {speaker} you? Save your voice so Poneva recognises you next time.',
  saveMyVoice: 'Yes, save my voice',

  soundsLike: 'Sounds like {name}',
  voiceMatch: 'Voice match',
  confirm: 'Confirm',
  notThisPerson: 'Not {name}',
  sameVoiceAs: 'Same voice as {speaker}?',
  bothSoundLike: '{a} and {b} both sound like {name}',
  mergeAndName: 'Merge and name',
  merge: 'Merge',
  notSame: 'Not the same',
  checkingVoices: 'Checking voices…',
  voiceCheckFailed: 'The voice check did not finish.',
  checkVoicesAgain: 'Check voices again',
  reviewUncertain: 'Review {count} uncertain rows',
  uncertainHint: 'This row may be {speaker}.',
  uncertainSpeaker: 'Uncertain speaker',
  keepAs: 'Keep as {name}',
  moveTo: 'Move to {name}',
  someoneElse: 'Someone else',
  skip: 'Skip',
  progress: '{n} of {total}',
  identificationOff: 'Voice recognition is off for this meeting.',
  turnOffVoiceSuggestions: 'Turn off voice recognition',
  useMyVoice: 'Use my voice from this meeting',
  isThisYou: 'Is this you?',
  promptTitle: 'Set up your voice signature?',
  promptSetUp: 'Set up',
  promptNoThanks: 'No thanks',
  consentLineVoice:
    'A voice signature can name you automatically in your company’s meetings when the match is strong, or suggest your name when it is less certain. It never approves or unlocks anything.',
  suggestNamesCheckbox: 'Suggest names from voice signatures',
  predatesVoicePass: 'This meeting was recorded before voice checks were available.',
  errorUnavailable: 'Voice checks are not available right now.',
  errorPassNotCurrent: 'Voices are being checked again. Try in a moment.',
  errorSuggestionGone: 'This suggestion is no longer available.',
};

const bg = {
  recognisedByVoice: 'Разпознат по гласа',
  undoRecognition: 'Отмяна',
  isSpeakerYou: 'Вие ли сте {speaker}? Запазете гласа си, за да ви разпознае Poneva следващия път.',
  saveMyVoice: 'Да, запази гласа ми',

  soundsLike: 'Звучи като {name}',
  voiceMatch: 'Гласово съвпадение',
  confirm: 'Потвърдете',
  notThisPerson: 'Не {name}',
  sameVoiceAs: 'Същият глас като {speaker}?',
  bothSoundLike: '{a} и {b} звучат като {name}',
  mergeAndName: 'Обединяване и име',
  merge: 'Обединяване',
  notSame: 'Не е същото',
  checkingVoices: 'Гласовете се проверяват...',
  voiceCheckFailed: 'Гласовата проверка не завърши.',
  checkVoicesAgain: 'Проверете отново гласовете',
  reviewUncertain: 'Прегледайте {count} несигурни редове',
  uncertainHint: 'Този ред може да бъде {speaker}.',
  uncertainSpeaker: 'Несигурен говорещ',
  keepAs: 'Запазете като {name}',
  moveTo: 'Преминете към {name}',
  someoneElse: 'някой друг',
  skip: 'Пропускане',
  progress: '{n} от {total}',
  identificationOff: 'Гласовото разпознаване е изключено за тази среща.',
  turnOffVoiceSuggestions: 'Изключете гласовото разпознаване',
  useMyVoice: 'Използвай гласа ми от тази среща',
  isThisYou: 'това ти ли си',
  promptTitle: 'Да настроите гласовия си подпис?',
  promptSetUp: 'Настройка',
  promptNoThanks: 'не благодаря',
  consentLineVoice:
    'Гласовият подпис може автоматично да добавя името ви в срещите на вашата компания при силно съвпадение или да го предлага при по-ниска увереност. Той никога не одобрява и не отключва нищо.',
  suggestNamesCheckbox: 'Предложете имена от гласови подписи',
  predatesVoicePass: 'Тази среща е записана преди гласовите проверки да са налични.',
  errorUnavailable: 'Гласовите проверки не са налични в момента.',
  errorPassNotCurrent: 'Гласовете се проверяват отново. Опитайте след малко.',
  errorSuggestionGone: 'Това предложение вече не е налично.',
};

const hr = {
  recognisedByVoice: 'Prepoznato po glasu',
  undoRecognition: 'Poništi',
  isSpeakerYou:
    'Jeste li vi {speaker}? Spremite svoj glas kako bi vas Poneva sljedeći put prepoznala.',
  saveMyVoice: 'Da, spremi moj glas',

  soundsLike: 'Zvuči kao {name}',
  voiceMatch: 'Glasovno podudaranje',
  confirm: 'Potvrdi',
  notThisPerson: 'Ne {name}',
  sameVoiceAs: 'Isti glas kao {speaker}?',
  bothSoundLike: '{a} i {b} zvuče kao {name}',
  mergeAndName: 'Spajanje i ime',
  merge: 'Spojiti',
  notSame: 'Nije isto',
  checkingVoices: 'Provjera glasova...',
  voiceCheckFailed: 'Glasovna provjera nije završila.',
  checkVoicesAgain: 'Ponovno provjerite glasove',
  reviewUncertain: 'Pregledajte {count} nesigurne retke',
  uncertainHint: 'Ovaj red može biti {speaker}.',
  uncertainSpeaker: 'Nesiguran govornik',
  keepAs: 'Zadrži kao {name}',
  moveTo: 'Premjesti u {name}',
  someoneElse: 'Netko drugi',
  skip: 'Preskočiti',
  progress: '{n} od {total}',
  identificationOff: 'Prepoznavanje glasa isključeno je za ovaj sastanak.',
  turnOffVoiceSuggestions: 'Isključi prepoznavanje glasa',
  useMyVoice: 'Koristi moj glas s ovog sastanka',
  isThisYou: 'Jesi li ovo ti?',
  promptTitle: 'Postaviti svoj glasovni potpis?',
  promptSetUp: 'Postaviti',
  promptNoThanks: 'ne hvala',
  consentLineVoice:
    'Glasovni potpis može automatski dodati vaše ime na sastancima vaše tvrtke kada je podudaranje snažno ili ga predložiti kada je manje sigurno. Nikada ništa ne odobrava niti otključava.',
  suggestNamesCheckbox: 'Predložite imena iz glasovnih potpisa',
  predatesVoicePass: 'Ovaj sastanak je snimljen prije nego što su glasovne provjere bile dostupne.',
  errorUnavailable: 'Glasovne provjere trenutačno nisu dostupne.',
  errorPassNotCurrent: 'Glasovi se ponovno provjeravaju. Pokušajte za koji trenutak.',
  errorSuggestionGone: 'Ovaj prijedlog više nije dostupan.',
};

const cs = {
  recognisedByVoice: 'Rozpoznáno podle hlasu',
  undoRecognition: 'Vrátit zpět',
  isSpeakerYou: 'Jste {speaker} vy? Uložte svůj hlas, aby vás Poneva příště poznala.',
  saveMyVoice: 'Ano, uložit můj hlas',

  soundsLike: 'Zní to jako {name}',
  voiceMatch: 'Shoda hlasu',
  confirm: 'Potvrdit',
  notThisPerson: 'Ne {name}',
  sameVoiceAs: 'Stejný hlas jako {speaker}?',
  bothSoundLike: '{a} a {b} znějí jako {name}',
  mergeAndName: 'Sloučit a pojmenovat',
  merge: 'Spojit',
  notSame: 'Ne totéž',
  checkingVoices: 'Kontrola hlasů…',
  voiceCheckFailed: 'Hlasová kontrola neskončila.',
  checkVoicesAgain: 'Znovu zkontrolujte hlasy',
  reviewUncertain: 'Zkontrolujte nejisté řádky {count}',
  uncertainHint: 'Tento řádek může být {speaker} .',
  uncertainSpeaker: 'Nejistý mluvčí',
  keepAs: 'Ponechat jako {name}',
  moveTo: 'Přejděte na {name}',
  someoneElse: 'Někdo jiný',
  skip: 'Přeskočit',
  progress: '{n} z {total}',
  identificationOff: 'Rozpoznávání hlasu je pro tuto schůzku vypnuté.',
  turnOffVoiceSuggestions: 'Vypnout rozpoznávání hlasu',
  useMyVoice: 'Použijte můj hlas z tohoto setkání',
  isThisYou: 'jsi to ty?',
  promptTitle: 'Nastavit hlasový podpis?',
  promptSetUp: 'Nastavit',
  promptNoThanks: 'Ne, děkuji',
  consentLineVoice:
    'Hlasový podpis vás může při silné shodě automaticky pojmenovat na schůzkách vaší firmy nebo při nižší jistotě vaše jméno navrhnout. Nikdy nic neschvaluje ani neodemyká.',
  suggestNamesCheckbox: 'Navrhujte jména z hlasových podpisů',
  predatesVoicePass: 'Tato schůzka byla nahrána dříve, než byly k dispozici hlasové kontroly.',
  errorUnavailable: 'Hlasové kontroly momentálně nejsou k dispozici.',
  errorPassNotCurrent: 'Hlasy se znovu kontrolují. Zkuste to za chvíli.',
  errorSuggestionGone: 'Tento návrh již není k dispozici.',
};

const da = {
  recognisedByVoice: 'Genkendt på stemmen',
  undoRecognition: 'Fortryd',
  isSpeakerYou: 'Er {speaker} dig? Gem din stemme, så Poneva genkender dig næste gang.',
  saveMyVoice: 'Ja, gem min stemme',

  soundsLike: 'Det lyder som {name}',
  voiceMatch: 'Stemmematch',
  confirm: 'Bekræfte',
  notThisPerson: 'Ikke {name}',
  sameVoiceAs: 'Samme stemme som {speaker}?',
  bothSoundLike: '{a} og {b} lyder begge som {name}',
  mergeAndName: 'Flet sammen og navngiv',
  merge: 'Flet sammen',
  notSame: 'Ikke det samme',
  checkingVoices: 'Tjekker stemmer...',
  voiceCheckFailed: 'Stemmetjekket blev ikke afsluttet.',
  checkVoicesAgain: 'Tjek stemmer igen',
  reviewUncertain: 'Gennemgå {count} usikre rækker',
  uncertainHint: 'Denne række kan være {speaker}.',
  uncertainSpeaker: 'Usikker taler',
  keepAs: 'Behold som {name}',
  moveTo: 'Flyt til {name}',
  someoneElse: 'En anden',
  skip: 'Springe',
  progress: '{n} af {total}',
  identificationOff: 'Stemmegenkendelse er slået fra for dette møde.',
  turnOffVoiceSuggestions: 'Slå stemmegenkendelse fra',
  useMyVoice: 'Brug min stemme fra dette møde',
  isThisYou: 'Er det dig?',
  promptTitle: 'Konfigurer din stemmesignatur?',
  promptSetUp: 'Indstil',
  promptNoThanks: 'Nej tak',
  consentLineVoice:
    'En stemmesignatur kan sætte dit navn på automatisk i virksomhedens møder ved et stærkt match eller foreslå det ved større usikkerhed. Den godkender eller låser aldrig noget op.',
  suggestNamesCheckbox: 'Foreslå navne fra stemmesignaturer',
  predatesVoicePass: 'Dette møde blev optaget, før stemmetjek var tilgængeligt.',
  errorUnavailable: 'Stemmetjek er ikke tilgængelig lige nu.',
  errorPassNotCurrent: 'Stemmer kontrolleres igen. Prøv om et øjeblik.',
  errorSuggestionGone: 'Dette forslag er ikke længere tilgængeligt.',
};

const nl = {
  recognisedByVoice: 'Herkend aan de stem',
  undoRecognition: 'Ongedaan maken',
  isSpeakerYou: 'Ben jij {speaker}? Sla je stem op zodat Poneva je de volgende keer herkent.',
  saveMyVoice: 'Ja, sla mijn stem op',

  soundsLike: 'Klinkt als {name}',
  voiceMatch: 'Stemovereenkomst',
  confirm: 'Bevestigen',
  notThisPerson: 'Niet {name}',
  sameVoiceAs: 'Dezelfde stem als {speaker}?',
  bothSoundLike: '{a} en {b} klinken allebei als {name}',
  mergeAndName: 'Samenvoegen en benoemen',
  merge: 'Samenvoegen',
  notSame: 'Niet hetzelfde',
  checkingVoices: 'Stemmen controleren...',
  voiceCheckFailed: 'De voicecheck is niet voltooid.',
  checkVoicesAgain: 'Controleer de stemmen opnieuw',
  reviewUncertain: 'Bekijk {count} onzekere rijen',
  uncertainHint: 'Deze rij kan {speaker} zijn.',
  uncertainSpeaker: 'Onzekere spreker',
  keepAs: 'Behouden als {name}',
  moveTo: 'Ga naar {name}',
  someoneElse: 'Iemand anders',
  skip: 'Overslaan',
  progress: '{n} van {total}',
  identificationOff: 'Stemherkenning is uitgeschakeld voor deze vergadering.',
  turnOffVoiceSuggestions: 'Stemherkenning uitschakelen',
  useMyVoice: 'Gebruik mijn stem uit deze bijeenkomst',
  isThisYou: 'Ben jij dit?',
  promptTitle: 'Uw stemhandtekening instellen?',
  promptSetUp: 'Instellen',
  promptNoThanks: 'Nee bedankt',
  consentLineVoice:
    'Een stemhandtekening kan je naam automatisch toevoegen in bedrijfsvergaderingen bij een sterke overeenkomst, of je naam voorstellen bij minder zekerheid. Ze keurt nooit iets goed en ontgrendelt niets.',
  suggestNamesCheckbox: 'Stel namen voor uit stemsignaturen',
  predatesVoicePass: 'Deze vergadering werd opgenomen voordat voicechecks beschikbaar waren.',
  errorUnavailable: 'Voicechecks zijn momenteel niet beschikbaar.',
  errorPassNotCurrent: 'Stemmen worden opnieuw gecontroleerd. Probeer het zo meteen.',
  errorSuggestionGone: 'Deze suggestie is niet langer beschikbaar.',
};

const et = {
  recognisedByVoice: 'Hääle järgi tuvastatud',
  undoRecognition: 'Võta tagasi',
  isSpeakerYou:
    'Kas {speaker} oled sina? Salvesta oma hääl, et Poneva tunneks su järgmisel korral ära.',
  saveMyVoice: 'Jah, salvesta minu hääl',

  soundsLike: 'Kõlab nagu {name}',
  voiceMatch: 'Hääle vaste',
  confirm: 'Kinnita',
  notThisPerson: 'Mitte {name}',
  sameVoiceAs: 'Sama hääl nagu {speaker}?',
  bothSoundLike: '{a} ja {b} kõlavad mõlemad nagu {name}',
  mergeAndName: 'Ühenda ja nimeta',
  merge: 'Ühendage',
  notSame: 'Mitte sama',
  checkingVoices: 'Häälte kontrollimine…',
  voiceCheckFailed: 'Häälekontroll ei lõppenud.',
  checkVoicesAgain: 'Kontrollige hääli uuesti',
  reviewUncertain: 'Vaadake üle {count} ebakindlad read',
  uncertainHint: 'See rida võib olla {speaker} .',
  uncertainSpeaker: 'Ebakindel kõneleja',
  keepAs: 'Säilita kui {name}',
  moveTo: 'Liikuge saidile {name}',
  someoneElse: 'Keegi teine',
  skip: 'Jäta vahele',
  progress: '{n} ja {total}',
  identificationOff: 'Hääletuvastus on sellel koosolekul välja lülitatud.',
  turnOffVoiceSuggestions: 'Lülita hääletuvastus välja',
  useMyVoice: 'Kasutage minu häält sellelt koosolekult',
  isThisYou: 'Kas see oled sina?',
  promptTitle: 'Kas seadistada oma häälallkiri?',
  promptSetUp: 'Seadistage',
  promptNoThanks: 'Ei aitäh',
  consentLineVoice:
    'Häälsignatuur võib tugeva vaste korral lisada ettevõtte koosolekutel teie nime automaatselt või ebakindlama vaste korral seda soovitada. See ei kinnita ega ava kunagi midagi.',
  suggestNamesCheckbox: 'Soovitage häälallkirjade põhjal nimesid',
  predatesVoicePass: 'See koosolek salvestati enne, kui häälekontrollid olid saadaval.',
  errorUnavailable: 'Häälkontrollid pole praegu saadaval.',
  errorPassNotCurrent: 'Hääled kontrollitakse uuesti. Proovige hetke pärast.',
  errorSuggestionGone: 'See soovitus pole enam saadaval.',
};

const fi = {
  recognisedByVoice: 'Tunnistettu äänestä',
  undoRecognition: 'Kumoa',
  isSpeakerYou:
    'Oletko sinä {speaker}? Tallenna äänesi, jotta Poneva tunnistaa sinut seuraavalla kerralla.',
  saveMyVoice: 'Kyllä, tallenna ääneni',

  soundsLike: 'Kuulostaa {name}:ltä',
  voiceMatch: 'Äänivastaavuus',
  confirm: 'Vahvistaa',
  notThisPerson: 'Ei {name}',
  sameVoiceAs: 'Sama ääni kuin {speaker}?',
  bothSoundLike: '{a} ja {b} kuulostavat molemmat {name}:ltä',
  mergeAndName: 'Yhdistä ja nimeä',
  merge: 'Yhdistää',
  notSame: 'Ei sama',
  checkingVoices: 'Tarkistetaan ääniä…',
  voiceCheckFailed: 'Äänitarkistus ei päättynyt.',
  checkVoicesAgain: 'Tarkista äänet uudelleen',
  reviewUncertain: 'Tarkista {count} epävarmat rivit',
  uncertainHint: 'Tämä rivi voi olla {speaker} .',
  uncertainSpeaker: 'Epävarma puhuja',
  keepAs: 'Säilytä muodossa {name}',
  moveTo: 'Siirry kohtaan {name}',
  someoneElse: 'Joku muu',
  skip: 'Ohita',
  progress: '{n} / {total}',
  identificationOff: 'Äänentunnistus on pois käytöstä tässä kokouksessa.',
  turnOffVoiceSuggestions: 'Poista äänentunnistus käytöstä',
  useMyVoice: 'Käytä ääntäni tästä kokouksesta',
  isThisYou: 'Oletko tämä sinä?',
  promptTitle: 'Määritetäänkö ääniallekirjoitus?',
  promptSetUp: 'Määritä',
  promptNoThanks: 'Ei kiitos',
  consentLineVoice:
    'Ääniallekirjoitus voi lisätä nimesi automaattisesti yrityksesi kokouksissa vahvan vastaavuuden perusteella tai ehdottaa sitä epävarmemmassa tapauksessa. Se ei koskaan hyväksy toimintoja tai avaa lukituksia.',
  suggestNamesCheckbox: 'Ehdota nimiä ääniallekirjoituksista',
  predatesVoicePass: 'Tämä kokous tallennettiin ennen kuin äänitarkistukset olivat saatavilla.',
  errorUnavailable: 'Äänitarkistukset eivät ole käytettävissä juuri nyt.',
  errorPassNotCurrent: 'Ääniä tarkistetaan uudelleen. Kokeile hetkessä.',
  errorSuggestionGone: 'Tämä ehdotus ei ole enää saatavilla.',
};

const fr = {
  recognisedByVoice: 'Reconnu à la voix',
  undoRecognition: 'Annuler',
  isSpeakerYou:
    'Êtes-vous {speaker} ? Enregistrez votre voix pour que Poneva vous reconnaisse la prochaine fois.',
  saveMyVoice: 'Oui, enregistrer ma voix',

  soundsLike: 'On dirait {name}',
  voiceMatch: 'Correspondance vocale',
  confirm: 'Confirmer',
  notThisPerson: 'Pas {name}',
  sameVoiceAs: 'Même voix que {speaker} ?',
  bothSoundLike: '{a} et {b} sonnent tous deux comme {name}',
  mergeAndName: 'Fusionner et nommer',
  merge: 'Fusionner',
  notSame: 'Pas pareil',
  checkingVoices: 'Vérification des voix…',
  voiceCheckFailed: "La vérification vocale ne s'est pas terminée.",
  checkVoicesAgain: 'Vérifiez à nouveau les voix',
  reviewUncertain: 'Examiner les lignes incertaines de {count}',
  uncertainHint: 'Cette ligne peut être {speaker} .',
  uncertainSpeaker: 'Intervenant incertain',
  keepAs: 'Conserver sous {name}',
  moveTo: 'Déplacer vers {name}',
  someoneElse: "Quelqu'un d'autre",
  skip: 'Sauter',
  progress: '{n} ou {total}',
  identificationOff: 'La reconnaissance vocale est désactivée pour cette réunion.',
  turnOffVoiceSuggestions: 'Désactiver la reconnaissance vocale',
  useMyVoice: 'Utiliser ma voix de cette réunion',
  isThisYou: 'Est-ce toi ?',
  promptTitle: 'Configurer votre signature vocale ?',
  promptSetUp: 'Installation',
  promptNoThanks: 'Non merci',
  consentLineVoice:
    'Une signature vocale peut vous nommer automatiquement dans les réunions de votre entreprise si la correspondance est forte, ou proposer votre nom si elle est moins certaine. Elle ne valide ni ne déverrouille jamais rien.',
  suggestNamesCheckbox: 'Suggérer des noms à partir de signatures vocales',
  predatesVoicePass:
    'Cette réunion a été enregistrée avant que les contrôles vocaux ne soient disponibles.',
  errorUnavailable: 'Les vérifications vocales ne sont pas disponibles pour le moment.',
  errorPassNotCurrent: 'Les voix sont à nouveau vérifiées. Essayez dans un instant.',
  errorSuggestionGone: "Cette suggestion n'est plus disponible.",
};

const de = {
  recognisedByVoice: 'An der Stimme erkannt',
  undoRecognition: 'Rückgängig',
  isSpeakerYou:
    'Sind Sie {speaker}? Speichern Sie Ihre Stimme, damit Poneva Sie beim nächsten Mal erkennt.',
  saveMyVoice: 'Ja, meine Stimme speichern',

  soundsLike: 'Klingt nach {name}',
  voiceMatch: 'Stimmenübereinstimmung',
  confirm: 'Bestätigen',
  notThisPerson: 'Nicht {name}',
  sameVoiceAs: 'Gleiche Stimme wie {speaker} ?',
  bothSoundLike: '{a} und {b} klingen beide wie {name}',
  mergeAndName: 'Zusammenführen und benennen',
  merge: 'Verschmelzen',
  notSame: 'Nicht dasselbe',
  checkingVoices: 'Stimmen prüfen…',
  voiceCheckFailed: 'Die Sprachprüfung wurde nicht abgeschlossen.',
  checkVoicesAgain: 'Überprüfen Sie die Stimmen erneut',
  reviewUncertain: 'Überprüfen Sie {count} unsichere Zeilen',
  uncertainHint: 'Diese Zeile kann {speaker} lauten.',
  uncertainSpeaker: 'Unsicherer Sprecher',
  keepAs: 'Als {name} behalten',
  moveTo: 'Gehen Sie zu {name}',
  someoneElse: 'Jemand anderes',
  skip: 'Überspringen',
  progress: '{n} von {total}',
  identificationOff: 'Die Stimmenerkennung ist für diese Besprechung ausgeschaltet.',
  turnOffVoiceSuggestions: 'Stimmenerkennung ausschalten',
  useMyVoice: 'Nutzen Sie meine Stimme aus diesem Treffen',
  isThisYou: 'Sind Sie das?',
  promptTitle: 'Richten Sie Ihre Sprachsignatur ein?',
  promptSetUp: 'Aufstellen',
  promptNoThanks: 'Nein danke',
  consentLineVoice:
    'Eine Stimmensignatur kann bei einer starken Übereinstimmung Ihren Namen in Firmenbesprechungen automatisch zuordnen oder ihn bei geringerer Sicherheit vorschlagen. Sie genehmigt und entsperrt niemals etwas.',
  suggestNamesCheckbox: 'Schlagen Sie Namen anhand von Sprachsignaturen vor',
  predatesVoicePass: 'Dieses Treffen wurde aufgezeichnet, bevor Stimmkontrollen verfügbar waren.',
  errorUnavailable: 'Sprachprüfungen sind derzeit nicht verfügbar.',
  errorPassNotCurrent: 'Stimmen werden erneut überprüft. Versuchen Sie es gleich.',
  errorSuggestionGone: 'Dieser Vorschlag ist nicht mehr verfügbar.',
};

const el = {
  recognisedByVoice: 'Αναγνωρίστηκε από τη φωνή',
  undoRecognition: 'Αναίρεση',
  isSpeakerYou:
    'Είστε εσείς ο/η {speaker}; Αποθηκεύστε τη φωνή σας για να σας αναγνωρίσει η Poneva την επόμενη φορά.',
  saveMyVoice: 'Ναι, αποθήκευση της φωνής μου',

  soundsLike: 'Ακούγεται σαν {name}',
  voiceMatch: 'Ταίριασμα φωνής',
  confirm: 'Επιβεβαιώνω',
  notThisPerson: 'Όχι {name}',
  sameVoiceAs: 'Ίδια φωνή με τον {speaker};',
  bothSoundLike: 'Τα {a} και {b} ακούγονται και τα δύο σαν {name}',
  mergeAndName: 'Συγχώνευση και όνομα',
  merge: 'Συγχώνευση',
  notSame: 'Όχι το ίδιο',
  checkingVoices: 'Έλεγχος φωνών…',
  voiceCheckFailed: 'Ο φωνητικός έλεγχος δεν ολοκληρώθηκε.',
  checkVoicesAgain: 'Ελέγξτε ξανά τις φωνές',
  reviewUncertain: 'Ελέγξτε τις αβέβαιες σειρές {count}',
  uncertainHint: 'Αυτή η σειρά μπορεί να είναι {speaker}.',
  uncertainSpeaker: 'Αβέβαιος ομιλητής',
  keepAs: 'Διατήρηση ως {name}',
  moveTo: 'Μετακίνηση στο {name}',
  someoneElse: 'Κάποιος άλλος',
  skip: 'Παραλείπω',
  progress: '{n} του {total}',
  identificationOff: 'Η αναγνώριση φωνής είναι απενεργοποιημένη για αυτή τη συνάντηση.',
  turnOffVoiceSuggestions: 'Απενεργοποίηση αναγνώρισης φωνής',
  useMyVoice: 'Χρησιμοποιήστε τη φωνή μου από αυτή τη συνάντηση',
  isThisYou: 'Εσύ είσαι αυτός;',
  promptTitle: 'Ρύθμιση της φωνητικής υπογραφής σας;',
  promptSetUp: 'Στήνω',
  promptNoThanks: 'Όχι ευχαριστώ',
  consentLineVoice:
    'Η φωνητική υπογραφή μπορεί να σας ονομάζει αυτόματα στις συναντήσεις της εταιρείας σας όταν η αντιστοίχιση είναι ισχυρή ή να προτείνει το όνομά σας όταν είναι λιγότερο βέβαιη. Δεν εγκρίνει ούτε ξεκλειδώνει ποτέ τίποτα.',
  suggestNamesCheckbox: 'Προτείνετε ονόματα από φωνητικές υπογραφές',
  predatesVoicePass: 'Αυτή η σύσκεψη ηχογραφήθηκε πριν γίνουν διαθέσιμοι φωνητικοί έλεγχοι.',
  errorUnavailable: 'Οι φωνητικοί έλεγχοι δεν είναι διαθέσιμοι αυτήν τη στιγμή.',
  errorPassNotCurrent: 'Οι φωνές ελέγχονται ξανά. Δοκιμάστε σε μια στιγμή.',
  errorSuggestionGone: 'Αυτή η πρόταση δεν είναι πλέον διαθέσιμη.',
};

const hu = {
  recognisedByVoice: 'Hang alapján felismerve',
  undoRecognition: 'Visszavonás',
  isSpeakerYou: 'Ön a(z) {speaker}? Mentse el a hangját, hogy a Poneva legközelebb felismerje.',
  saveMyVoice: 'Igen, mentse el a hangomat',

  soundsLike: 'Úgy hangzik, mint a {name}',
  voiceMatch: 'Hangegyezés',
  confirm: 'Erősítse meg',
  notThisPerson: 'Nem {name}',
  sameVoiceAs: 'Ugyanaz a hang, mint a {speaker} ?',
  bothSoundLike: 'A {a} és a {b} egyaránt úgy hangzik, mint a {name}',
  mergeAndName: 'Egyesítés és név',
  merge: 'Összeolvad',
  notSame: 'Nem ugyanaz',
  checkingVoices: 'Hangok ellenőrzése…',
  voiceCheckFailed: 'A hangellenőrzés nem fejeződött be.',
  checkVoicesAgain: 'Ellenőrizze újra a hangokat',
  reviewUncertain: 'Tekintse át a {count} bizonytalan sorokat',
  uncertainHint: 'Ez a sor lehet {speaker} .',
  uncertainSpeaker: 'Bizonytalan beszélő',
  keepAs: 'Megtartani {name} néven',
  moveTo: 'Ugrás ide: {name}',
  someoneElse: 'Valaki más',
  skip: 'Kihagyás',
  progress: '{n}/{total}',
  identificationOff: 'A hangfelismerés ki van kapcsolva ezen a megbeszélésen.',
  turnOffVoiceSuggestions: 'Hangfelismerés kikapcsolása',
  useMyVoice: 'Használd a hangomat erről a találkozóról',
  isThisYou: 'te vagy ez?',
  promptTitle: 'Beállítja a hangaláírását?',
  promptSetUp: 'Állítsa be',
  promptNoThanks: 'Nem köszönöm',
  consentLineVoice:
    'A hangaláírás erős egyezés esetén automatikusan hozzárendelheti a nevét a céges megbeszéléseken, bizonytalanabb egyezésnél pedig javasolhatja azt. Soha nem hagy jóvá és nem old fel semmit.',
  suggestNamesCheckbox: 'Javasoljon neveket hangaláírásokból',
  predatesVoicePass:
    'Ezt a találkozót azelőtt rögzítették, hogy a hangellenőrzések elérhetőek lettek volna.',
  errorUnavailable: 'A hangellenőrzés jelenleg nem érhető el.',
  errorPassNotCurrent: 'A hangokat ismét ellenőrzik. Próbáld meg egy pillanat alatt.',
  errorSuggestionGone: 'Ez a javaslat már nem érhető el.',
};

const ga = {
  recognisedByVoice: 'Aitheanta ón nguth',
  undoRecognition: 'Cealaigh',
  isSpeakerYou:
    'An tusa {speaker}? Sábháil do ghuth ionas go n-aithneoidh Poneva thú an chéad uair eile.',
  saveMyVoice: 'Sea, sábháil mo ghuth',

  soundsLike: 'Fuaimeanna cosúil le {name}',
  voiceMatch: 'Meaitseáil gutha',
  confirm: 'Deimhnigh',
  notThisPerson: 'Ní {name}',
  sameVoiceAs: 'An guth céanna le {speaker} ?',
  bothSoundLike: 'Fuaimeann {a} agus {b} araon {name}',
  mergeAndName: 'Cumaisc agus ainm',
  merge: 'Cumaisc',
  notSame: 'Ní mar an gcéanna',
  checkingVoices: 'Guthanna á seiceáil…',
  voiceCheckFailed: 'Níor chríochnaigh an tseiceáil gutha.',
  checkVoicesAgain: 'Seiceáil guthanna arís',
  reviewUncertain: 'Déan athbhreithniú ar {count} sraitheanna neamhchinnte',
  uncertainHint: 'Seans gur {speaker} an tsraith seo.',
  uncertainSpeaker: 'Cainteoir éiginnte',
  keepAs: 'Coinnigh mar {name}',
  moveTo: 'Bog go {name}',
  someoneElse: 'Duine éigin eile',
  skip: 'Scipeáil',
  progress: '{n} de {total}',
  identificationOff: 'Tá aithint gutha múchta don chruinniú seo.',
  turnOffVoiceSuggestions: 'Aithint gutha a mhúchadh',
  useMyVoice: 'Bain úsáid as mo ghuth ón gcruinniú seo',
  isThisYou: 'An é seo tú?',
  promptTitle: 'Socraigh do shíniú gutha?',
  promptSetUp: 'Socraigh suas',
  promptNoThanks: 'Níl go raibh maith agat',
  consentLineVoice:
    'Is féidir le síniú gutha d’ainm a chur i bhfeidhm go huathoibríoch i gcruinnithe do chuideachta nuair atá an mheaitseáil láidir, nó é a mholadh nuair atá sí níos neamhchinnte. Ní thugann sé cead ná ní dhíghlasálann sé aon rud riamh.',
  suggestNamesCheckbox: 'Mol ainmneacha ó shínithe gutha',
  predatesVoicePass: 'Taifeadadh an cruinniú seo sula raibh seiceálacha gutha ar fáil.',
  errorUnavailable: 'Níl seiceálacha gutha ar fáil faoi láthair.',
  errorPassNotCurrent: 'Tá guthanna á sheiceáil arís. Bain triail as i nóiméad.',
  errorSuggestionGone: 'Níl an moladh seo ar fáil a thuilleadh.',
};

const it = {
  recognisedByVoice: 'Riconosciuto dalla voce',
  undoRecognition: 'Annulla',
  isSpeakerYou:
    'Sei tu {speaker}? Salva la tua voce per farti riconoscere da Poneva la prossima volta.',
  saveMyVoice: 'Sì, salva la mia voce',

  soundsLike: 'Sembra {name}',
  voiceMatch: 'Corrispondenza vocale',
  confirm: 'Confermare',
  notThisPerson: 'Non {name}',
  sameVoiceAs: 'Stessa voce di {speaker} ?',
  bothSoundLike: '{a} e {b} suonano entrambi come {name}',
  mergeAndName: 'Unisci e nomina',
  merge: 'Unisci',
  notSame: 'Non è lo stesso',
  checkingVoices: 'Controllo delle voci…',
  voiceCheckFailed: 'Il controllo vocale non è terminato.',
  checkVoicesAgain: 'Controlla di nuovo le voci',
  reviewUncertain: 'Rivedere {count} righe incerte',
  uncertainHint: 'Questa riga potrebbe essere {speaker} .',
  uncertainSpeaker: 'Parlante incerto',
  keepAs: 'Mantieni come {name}',
  moveTo: 'Passare a {name}',
  someoneElse: 'Qualcun altro',
  skip: 'Saltare',
  progress: '{n} di {total}',
  identificationOff: 'Il riconoscimento vocale è disattivato per questa riunione.',
  turnOffVoiceSuggestions: 'Disattiva il riconoscimento vocale',
  useMyVoice: 'Usa la mia voce da questo incontro',
  isThisYou: 'Sei tu?',
  promptTitle: 'Vuoi impostare la tua firma vocale?',
  promptSetUp: 'Impostare',
  promptNoThanks: 'No grazie',
  consentLineVoice:
    'Una firma vocale può assegnare automaticamente il tuo nome nelle riunioni aziendali se la corrispondenza è forte, oppure suggerirlo se è meno certa. Non approva e non sblocca mai nulla.',
  suggestNamesCheckbox: 'Suggerisci nomi dalle firme vocali',
  predatesVoicePass:
    'Questa riunione è stata registrata prima che fossero disponibili i controlli vocali.',
  errorUnavailable: 'I controlli vocali non sono disponibili al momento.',
  errorPassNotCurrent: 'Le voci vengono nuovamente controllate. Prova tra un attimo.',
  errorSuggestionGone: 'Questo suggerimento non è più disponibile.',
};

const lv = {
  recognisedByVoice: 'Atpazīts pēc balss',
  undoRecognition: 'Atsaukt',
  isSpeakerYou:
    'Vai {speaker} esat jūs? Saglabājiet savu balsi, lai Poneva jūs nākamreiz atpazītu.',
  saveMyVoice: 'Jā, saglabāt manu balsi',

  soundsLike: 'Izklausās pēc {name}',
  voiceMatch: 'Balss atbilstība',
  confirm: 'Apstiprināt',
  notThisPerson: 'Nevis {name}',
  sameVoiceAs: 'Tāda pati balss kā {speaker} ?',
  bothSoundLike: '{a} un {b} abi izklausās kā {name}',
  mergeAndName: 'Apvienot un nosaukt',
  merge: 'Apvienot',
  notSame: 'Nav tas pats',
  checkingVoices: 'Notiek balsu pārbaude…',
  voiceCheckFailed: 'Balss pārbaude netika pabeigta.',
  checkVoicesAgain: 'Vēlreiz pārbaudiet balsis',
  reviewUncertain: 'Pārskatiet {count} nenoteiktās rindas',
  uncertainHint: 'Šī rinda var būt {speaker} .',
  uncertainSpeaker: 'Neskaidrs runātājs',
  keepAs: 'Saglabāt kā {name}',
  moveTo: 'Pārvietojieties uz {name}',
  someoneElse: 'Kāds cits',
  skip: 'Izlaist',
  progress: '{n} no {total}',
  identificationOff: 'Šai sapulcei balss atpazīšana ir izslēgta.',
  turnOffVoiceSuggestions: 'Izslēgt balss atpazīšanu',
  useMyVoice: 'Izmantojiet manu balsi no šīs sapulces',
  isThisYou: 'Vai tas esi tu?',
  promptTitle: 'Vai iestatīt balss parakstu?',
  promptSetUp: 'Iestatīt',
  promptNoThanks: 'Nē, paldies',
  consentLineVoice:
    'Balss paraksts var automātiski pievienot jūsu vārdu uzņēmuma sapulcēs, ja atbilstība ir spēcīga, vai ieteikt to, ja pārliecība ir mazāka. Tas nekad neko neapstiprina un neatbloķē.',
  suggestNamesCheckbox: 'Ieteikt vārdus no balss parakstiem',
  predatesVoicePass: 'Šī sapulce tika ierakstīta, pirms bija pieejamas balss pārbaudes.',
  errorUnavailable: 'Balss pārbaudes pašlaik nav pieejamas.',
  errorPassNotCurrent: 'Balsis tiek vēlreiz pārbaudītas. Mēģiniet pēc brīža.',
  errorSuggestionGone: 'Šis ieteikums vairs nav pieejams.',
};

const lt = {
  recognisedByVoice: 'Atpažinta pagal balsą',
  undoRecognition: 'Atšaukti',
  isSpeakerYou:
    'Ar {speaker} esate jūs? Išsaugokite savo balsą, kad Poneva jus atpažintų kitą kartą.',
  saveMyVoice: 'Taip, išsaugoti mano balsą',

  soundsLike: 'Skamba kaip {name}',
  voiceMatch: 'Balso atitikmuo',
  confirm: 'Patvirtinti',
  notThisPerson: 'Ne {name}',
  sameVoiceAs: 'Tas pats balsas kaip {speaker}?',
  bothSoundLike: '{a} ir {b} skamba kaip {name}',
  mergeAndName: 'Sujungti ir pavadinti',
  merge: 'Sujungti',
  notSame: 'Ne tas pats',
  checkingVoices: 'Tikrinami balsai…',
  voiceCheckFailed: 'Balso patikra nesibaigė.',
  checkVoicesAgain: 'Dar kartą patikrinkite balsus',
  reviewUncertain: 'Peržiūrėkite neaiškias eilutes {count}',
  uncertainHint: 'Ši eilutė gali būti {speaker} .',
  uncertainSpeaker: 'Neaiškus kalbėtojas',
  keepAs: 'Laikyti kaip {name}',
  moveTo: 'Perkelkite į {name}',
  someoneElse: 'Kas nors kitas',
  skip: 'Praleisti',
  progress: '{n} iš {total}',
  identificationOff: 'Šiame susitikime balso atpažinimas išjungtas.',
  turnOffVoiceSuggestions: 'Išjungti balso atpažinimą',
  useMyVoice: 'Naudokite mano balsą iš šio susitikimo',
  isThisYou: 'Ar tai tu?',
  promptTitle: 'Nustatyti balso parašą?',
  promptSetUp: 'Nustatyti',
  promptNoThanks: 'Ne, ačiū',
  consentLineVoice:
    'Balso parašas gali automatiškai priskirti jūsų vardą įmonės susitikimuose, kai atitiktis stipri, arba pasiūlyti jį, kai ji mažiau patikima. Jis niekada nieko nepatvirtina ir neatrakina.',
  suggestNamesCheckbox: 'Siūlykite vardus iš balso parašų',
  predatesVoicePass: 'Šis susitikimas buvo įrašytas dar nepasiekus balso patikros.',
  errorUnavailable: 'Patikros balsu šiuo metu nepasiekiamos.',
  errorPassNotCurrent: 'Balsai vėl tikrinami. Pabandykite akimirksniu.',
  errorSuggestionGone: 'Šis pasiūlymas nebepasiekiamas.',
};

const mt = {
  recognisedByVoice: 'Magħruf mill-vuċi',
  undoRecognition: 'Ħassar il-bidla',
  isSpeakerYou: 'Inti {speaker}? Issejvja leħnek biex Poneva tagħrfek id-darba li jmiss.',
  saveMyVoice: 'Iva, issejvja leħni',

  soundsLike: 'Ħsejjes bħal {name}',
  voiceMatch: 'Tqabbil tal-vuċi',
  confirm: 'Ikkonferma',
  notThisPerson: 'Mhux {name}',
  sameVoiceAs: 'L-istess vuċi bħal {speaker} ?',
  bothSoundLike: '{a} u {b} it-tnejn ħoss bħal {name}',
  mergeAndName: 'Għaqda u isem',
  merge: 'Għaqda',
  notSame: 'Mhux l-istess',
  checkingVoices: 'Qed niċċekkja l-vuċijiet...',
  voiceCheckFailed: 'Il-kontroll tal-vuċi ma spiċċax.',
  checkVoicesAgain: 'Iċċekkja l-vuċijiet mill-ġdid',
  reviewUncertain: 'Irrevedi {count} ringieli inċerti',
  uncertainHint: "Din ir-ringiela tista' tkun {speaker}.",
  uncertainSpeaker: 'Kelliem mhux ċert',
  keepAs: 'Żomm bħala {name}',
  moveTo: 'Imxi lejn {name}',
  someoneElse: 'Xi ħadd ieħor',
  skip: 'Aqbeż',
  progress: '{n} ta {total}',
  identificationOff: 'Ir-rikonoxximent tal-vuċi huwa mitfi għal din il-laqgħa.',
  turnOffVoiceSuggestions: 'Itfi r-rikonoxximent tal-vuċi',
  useMyVoice: 'Uża leħni minn din il-laqgħa',
  isThisYou: 'Dan int?',
  promptTitle: 'Twaqqaf il-firma tal-vuċi tiegħek?',
  promptSetUp: 'Imwaqqaf',
  promptNoThanks: 'Le grazzi',
  consentLineVoice:
    'Firma tal-vuċi tista’ tassenja ismek awtomatikament fil-laqgħat tal-kumpanija tiegħek meta t-tqabbil ikun qawwi, jew tissuġġerih meta jkun inqas ċert. Qatt ma tapprova jew tiftaħ xi ħaġa.',
  suggestNamesCheckbox: 'Issuġġerixxi ismijiet mill-firem tal-vuċi',
  predatesVoicePass:
    'Din il-laqgħa ġiet irreġistrata qabel ma kienu disponibbli kontrolli tal-vuċi.',
  errorUnavailable: 'Voice checks mhumiex disponibbli bħalissa.',
  errorPassNotCurrent: "Il-vuċijiet qed jerġgħu jiġu ċċekkjati. Ipprova f'mument.",
  errorSuggestionGone: "Dan is-suġġeriment m'għadux disponibbli.",
};

const pl = {
  recognisedByVoice: 'Rozpoznano po głosie',
  undoRecognition: 'Cofnij',
  isSpeakerYou: 'Czy {speaker} to Ty? Zapisz swój głos, aby Poneva rozpoznała Cię następnym razem.',
  saveMyVoice: 'Tak, zapisz mój głos',

  soundsLike: 'Brzmi jak {name}',
  voiceMatch: 'Dopasowanie głosu',
  confirm: 'Potwierdzać',
  notThisPerson: 'Nie {name}',
  sameVoiceAs: 'Ten sam głos co {speaker}?',
  bothSoundLike: '{a} i {b} brzmią jak {name}',
  mergeAndName: 'Połącz i nazwij',
  merge: 'Łączyć',
  notSame: 'Nie to samo',
  checkingVoices: 'Sprawdzam głosy…',
  voiceCheckFailed: 'Sprawdzanie głosu nie zostało zakończone.',
  checkVoicesAgain: 'Sprawdź głosy ponownie',
  reviewUncertain: 'Przejrzyj niepewne wiersze {count}',
  uncertainHint: 'Ten wiersz może mieć postać {speaker} .',
  uncertainSpeaker: 'Niepewny mówca',
  keepAs: 'Zachowaj jako {name}',
  moveTo: 'Przejdź do {name}',
  someoneElse: 'Ktoś inny',
  skip: 'Pominąć',
  progress: '{n} z {total}',
  identificationOff: 'Rozpoznawanie głosu jest wyłączone dla tego spotkania.',
  turnOffVoiceSuggestions: 'Wyłącz rozpoznawanie głosu',
  useMyVoice: 'Użyj mojego głosu z tego spotkania',
  isThisYou: 'Czy to ty?',
  promptTitle: 'Skonfigurować swój podpis głosowy?',
  promptSetUp: 'Organizować coś',
  promptNoThanks: 'Nie, dziękuję',
  consentLineVoice:
    'Podpis głosowy może automatycznie przypisać Twoje imię na spotkaniach firmowych przy silnym dopasowaniu lub zasugerować je przy mniejszej pewności. Nigdy niczego nie zatwierdza ani nie odblokowuje.',
  suggestNamesCheckbox: 'Sugeruj nazwy na podstawie podpisów głosowych',
  predatesVoicePass: 'Spotkanie zostało nagrane, zanim udostępniono kontrolę głosową.',
  errorUnavailable: 'Sprawdzanie głosowe nie jest obecnie dostępne.',
  errorPassNotCurrent: 'Głosy są ponownie sprawdzane. Spróbuj za chwilę.',
  errorSuggestionGone: 'Ta sugestia nie jest już dostępna.',
};

const pt = {
  recognisedByVoice: 'Reconhecido pela voz',
  undoRecognition: 'Anular',
  isSpeakerYou: 'É você {speaker}? Guarde a sua voz para que a Poneva o reconheça da próxima vez.',
  saveMyVoice: 'Sim, guardar a minha voz',

  soundsLike: 'Parece {name}',
  voiceMatch: 'Correspondência de voz',
  confirm: 'Confirmar',
  notThisPerson: 'Não {name}',
  sameVoiceAs: 'Mesma voz de {speaker}?',
  bothSoundLike: '{a} e {b} soam como {name}',
  mergeAndName: 'Mesclar e nomear',
  merge: 'Mesclar',
  notSame: 'Não é o mesmo',
  checkingVoices: 'Verificando vozes…',
  voiceCheckFailed: 'A verificação de voz não terminou.',
  checkVoicesAgain: 'Verifique as vozes novamente',
  reviewUncertain: 'Revise linhas incertas de {count}',
  uncertainHint: 'Esta linha pode ser {speaker} .',
  uncertainSpeaker: 'Orador incerto',
  keepAs: 'Manter como {name}',
  moveTo: 'Mude para {name}',
  someoneElse: 'Outra pessoa',
  skip: 'Pular',
  progress: '{n} de {total}',
  identificationOff: 'O reconhecimento de voz está desativado para esta reunião.',
  turnOffVoiceSuggestions: 'Desativar o reconhecimento de voz',
  useMyVoice: 'Use minha voz nesta reunião',
  isThisYou: 'É você?',
  promptTitle: 'Configurar sua assinatura de voz?',
  promptSetUp: 'Configurar',
  promptNoThanks: 'Não, obrigado',
  consentLineVoice:
    'Uma assinatura de voz pode atribuir automaticamente o seu nome nas reuniões da empresa quando a correspondência é forte, ou sugeri-lo quando é menos certa. Nunca aprova nem desbloqueia nada.',
  suggestNamesCheckbox: 'Sugerir nomes de assinaturas de voz',
  predatesVoicePass:
    'Esta reunião foi gravada antes que as verificações de voz estivessem disponíveis.',
  errorUnavailable: 'As verificações de voz não estão disponíveis no momento.',
  errorPassNotCurrent: 'As vozes estão sendo verificadas novamente. Experimente daqui a pouco.',
  errorSuggestionGone: 'Esta sugestão não está mais disponível.',
};

const ro = {
  recognisedByVoice: 'Recunoscut după voce',
  undoRecognition: 'Anulează',
  isSpeakerYou:
    'Tu ești {speaker}? Salvează-ți vocea pentru ca Poneva să te recunoască data viitoare.',
  saveMyVoice: 'Da, salvează-mi vocea',

  soundsLike: 'Sună ca {name}',
  voiceMatch: 'Potrivire vocală',
  confirm: 'Confirma',
  notThisPerson: 'Nu {name}',
  sameVoiceAs: 'Aceeași voce ca {speaker}?',
  bothSoundLike: '{a} și {b} sună ambele ca {name}',
  mergeAndName: 'Îmbinați și denumiți',
  merge: 'Îmbinați',
  notSame: 'Nu la fel',
  checkingVoices: 'Se verifică vocile...',
  voiceCheckFailed: 'Verificarea vocală nu s-a terminat.',
  checkVoicesAgain: 'Verificați din nou vocile',
  reviewUncertain: 'Examinați {count} rândurile incerte',
  uncertainHint: 'Acest rând poate fi {speaker} .',
  uncertainSpeaker: 'Vorbitor incert',
  keepAs: 'Păstrați ca {name}',
  moveTo: 'Mutați la {name}',
  someoneElse: 'altcineva',
  skip: 'Sari peste',
  progress: '{n} din {total}',
  identificationOff: 'Recunoașterea vocală este dezactivată pentru această ședință.',
  turnOffVoiceSuggestions: 'Dezactivați recunoașterea vocală',
  useMyVoice: 'Folosește-mi vocea de la această întâlnire',
  isThisYou: 'tu esti asta?',
  promptTitle: 'Configurați semnătura vocală?',
  promptSetUp: 'Înființat',
  promptNoThanks: 'Nu, mulțumesc',
  consentLineVoice:
    'O semnătură vocală poate atribui automat numele dvs. în ședințele companiei când potrivirea este puternică sau îl poate sugera când este mai puțin sigură. Nu aprobă și nu deblochează niciodată nimic.',
  suggestNamesCheckbox: 'Sugerați nume din semnăturile vocale',
  predatesVoicePass:
    'Această întâlnire a fost înregistrată înainte ca verificările vocale să fie disponibile.',
  errorUnavailable: 'Verificările vocale nu sunt disponibile momentan.',
  errorPassNotCurrent: 'Vocile sunt verificate din nou. Încearcă într-o clipă.',
  errorSuggestionGone: 'Această sugestie nu mai este disponibilă.',
};

const sk = {
  recognisedByVoice: 'Rozpoznané podľa hlasu',
  undoRecognition: 'Vrátiť späť',
  isSpeakerYou: 'Ste {speaker} vy? Uložte svoj hlas, aby vás Poneva nabudúce spoznala.',
  saveMyVoice: 'Áno, uložiť môj hlas',

  soundsLike: 'Znie to ako {name}',
  voiceMatch: 'Zhoda hlasu',
  confirm: 'Potvrďte',
  notThisPerson: 'Nie {name}',
  sameVoiceAs: 'Rovnaký hlas ako {speaker}?',
  bothSoundLike: '{a} a {b} znejú ako {name}',
  mergeAndName: 'Zlúčiť a pomenovať',
  merge: 'Zlúčiť',
  notSame: 'Nie to isté',
  checkingVoices: 'Kontrolujú sa hlasy…',
  voiceCheckFailed: 'Hlasová kontrola sa neskončila.',
  checkVoicesAgain: 'Znova skontrolujte hlasy',
  reviewUncertain: 'Skontrolujte neisté riadky {count}',
  uncertainHint: 'Tento riadok môže byť {speaker} .',
  uncertainSpeaker: 'Neistý rečník',
  keepAs: 'Ponechať ako {name}',
  moveTo: 'Prejdite na {name}',
  someoneElse: 'Niekto iný',
  skip: 'Preskočiť',
  progress: '{n} z {total}',
  identificationOff: 'Rozpoznávanie hlasu je pre toto stretnutie vypnuté.',
  turnOffVoiceSuggestions: 'Vypnúť rozpoznávanie hlasu',
  useMyVoice: 'Použite môj hlas z tohto stretnutia',
  isThisYou: 'si to ty?',
  promptTitle: 'Nastaviť hlasový podpis?',
  promptSetUp: 'Nastaviť',
  promptNoThanks: 'Nie, ďakujem',
  consentLineVoice:
    'Hlasový podpis vás môže pri silnej zhode automaticky pomenovať na firemných stretnutiach alebo pri nižšej istote navrhnúť vaše meno. Nikdy nič neschvaľuje ani neodomyká.',
  suggestNamesCheckbox: 'Navrhujte mená z hlasových podpisov',
  predatesVoicePass:
    'Toto stretnutie bolo zaznamenané predtým, ako boli k dispozícii hlasové kontroly.',
  errorUnavailable: 'Hlasové kontroly momentálne nie sú k dispozícii.',
  errorPassNotCurrent: 'Hlasy sa znova kontrolujú. Skúste o chvíľu.',
  errorSuggestionGone: 'Tento návrh už nie je k dispozícii.',
};

const sl = {
  recognisedByVoice: 'Prepoznano po glasu',
  undoRecognition: 'Razveljavi',
  isSpeakerYou: 'Ste vi {speaker}? Shranite svoj glas, da vas Poneva naslednjič prepozna.',
  saveMyVoice: 'Da, shrani moj glas',

  soundsLike: 'Sliši se kot {name}',
  voiceMatch: 'Glasovno ujemanje',
  confirm: 'Potrdi',
  notThisPerson: 'Ne {name}',
  sameVoiceAs: 'Isti glas kot {speaker}?',
  bothSoundLike: '{a} in {b} zvenita kot {name}',
  mergeAndName: 'Spoji in poimenuj',
  merge: 'Spoji',
  notSame: 'Ni enako',
  checkingVoices: 'Preverjanje glasov ...',
  voiceCheckFailed: 'Glasovno preverjanje se ni končalo.',
  checkVoicesAgain: 'Ponovno preveri glasove',
  reviewUncertain: 'Preglejte negotove vrstice {count}',
  uncertainHint: 'Ta vrstica je lahko {speaker}.',
  uncertainSpeaker: 'Negotov govorec',
  keepAs: 'Ohrani kot {name}',
  moveTo: 'Premakni se na {name}',
  someoneElse: 'Nekdo drug',
  skip: 'Preskoči',
  progress: '{n} od {total}',
  identificationOff: 'Prepoznavanje glasu je za ta sestanek izklopljeno.',
  turnOffVoiceSuggestions: 'Izklopi prepoznavanje glasu',
  useMyVoice: 'Uporabi moj glas s tega sestanka',
  isThisYou: 'si to ti',
  promptTitle: 'Želite nastaviti svoj glasovni podpis?',
  promptSetUp: 'Nastavite',
  promptNoThanks: 'ne hvala',
  consentLineVoice:
    'Glasovni podpis vas lahko pri močnem ujemanju samodejno poimenuje na sestankih podjetja ali pri manjši gotovosti predlaga vaše ime. Nikoli ničesar ne odobri ali odklene.',
  suggestNamesCheckbox: 'Predlagajte imena iz glasovnih podpisov',
  predatesVoicePass: 'Ta sestanek je bil posnet, preden so bila na voljo glasovna preverjanja.',
  errorUnavailable: 'Glasovna preverjanja trenutno niso na voljo.',
  errorPassNotCurrent: 'Glasovi se ponovno preverjajo. Poskusite v trenutku.',
  errorSuggestionGone: 'Ta predlog ni več na voljo.',
};

const es = {
  recognisedByVoice: 'Reconocido por la voz',
  undoRecognition: 'Deshacer',
  isSpeakerYou: '¿Eres tú {speaker}? Guarda tu voz para que Poneva te reconozca la próxima vez.',
  saveMyVoice: 'Sí, guardar mi voz',

  soundsLike: 'Suena como {name}',
  voiceMatch: 'Coincidencia de voz',
  confirm: 'Confirmar',
  notThisPerson: 'No {name}',
  sameVoiceAs: '¿La misma voz que {speaker}?',
  bothSoundLike: '{a} y {b} suenan como {name}',
  mergeAndName: 'Fusionar y nombrar',
  merge: 'Unir',
  notSame: 'No es lo mismo',
  checkingVoices: 'Comprobando voces...',
  voiceCheckFailed: 'La verificación de voz no terminó.',
  checkVoicesAgain: 'Revisa las voces nuevamente',
  reviewUncertain: 'Revisar filas inciertas {count}',
  uncertainHint: 'Esta fila puede ser {speaker}.',
  uncertainSpeaker: 'Hablante incierto',
  keepAs: 'Mantener como {name}',
  moveTo: 'Mover a {name}',
  someoneElse: 'Alguien más',
  skip: 'Saltar',
  progress: '{n} de {total}',
  identificationOff: 'El reconocimiento de voz está desactivado para esta reunión.',
  turnOffVoiceSuggestions: 'Desactivar el reconocimiento de voz',
  useMyVoice: 'Usa mi voz de esta reunión',
  isThisYou: '¿Eres tú?',
  promptTitle: '¿Configurar tu firma de voz?',
  promptSetUp: 'Configuración',
  promptNoThanks: 'No, gracias',
  consentLineVoice:
    'Una firma de voz puede asignar tu nombre automáticamente en las reuniones de tu empresa si la coincidencia es fuerte, o sugerirlo si es menos segura. Nunca aprueba ni desbloquea nada.',
  suggestNamesCheckbox: 'Sugerir nombres a partir de firmas de voz.',
  predatesVoicePass:
    'Esta reunión se grabó antes de que estuvieran disponibles las comprobaciones de voz.',
  errorUnavailable: 'Las comprobaciones de voz no están disponibles en este momento.',
  errorPassNotCurrent: 'Se están revisando las voces nuevamente. Pruébalo en un momento.',
  errorSuggestionGone: 'Esta sugerencia ya no está disponible.',
};

const sv = {
  recognisedByVoice: 'Igenkänd på rösten',
  undoRecognition: 'Ångra',
  isSpeakerYou: 'Är {speaker} du? Spara din röst så att Poneva känner igen dig nästa gång.',
  saveMyVoice: 'Ja, spara min röst',

  soundsLike: 'Låter som {name}',
  voiceMatch: 'Röstmatchning',
  confirm: 'Bekräfta',
  notThisPerson: 'Inte {name}',
  sameVoiceAs: 'Samma röst som {speaker}?',
  bothSoundLike: '{a} och {b} låter båda som {name}',
  mergeAndName: 'Slå samman och namnge',
  merge: 'Slå ihop',
  notSame: 'Inte samma sak',
  checkingVoices: 'Kontrollerar röster...',
  voiceCheckFailed: 'Röstkontrollen slutfördes inte.',
  checkVoicesAgain: 'Kontrollera rösterna igen',
  reviewUncertain: 'Granska {count} osäkra rader',
  uncertainHint: 'Den här raden kan vara {speaker}.',
  uncertainSpeaker: 'Osäker talare',
  keepAs: 'Behåll som {name}',
  moveTo: 'Flytta till {name}',
  someoneElse: 'Någon annan',
  skip: 'Hoppa',
  progress: '{n} av {total}',
  identificationOff: 'Röstigenkänning är avstängd för det här mötet.',
  turnOffVoiceSuggestions: 'Stäng av röstigenkänning',
  useMyVoice: 'Använd min röst från det här mötet',
  isThisYou: 'Är det här du?',
  promptTitle: 'Konfigurera din röstsignatur?',
  promptSetUp: 'Inrätta',
  promptNoThanks: 'Nej tack',
  consentLineVoice:
    'En röstsignatur kan namnge dig automatiskt i företagets möten vid en stark matchning, eller föreslå ditt namn när matchningen är mindre säker. Den godkänner eller låser aldrig upp något.',
  suggestNamesCheckbox: 'Föreslå namn från röstsignaturer',
  predatesVoicePass: 'Detta möte spelades in innan röstkontroller var tillgängliga.',
  errorUnavailable: 'Röstkontroller är inte tillgängliga just nu.',
  errorPassNotCurrent: 'Röster kontrolleras igen. Försök om ett ögonblick.',
  errorSuggestionGone: 'Detta förslag är inte längre tillgängligt.',
};

const meetingsvoiceCopy: NamespaceCatalog<MeetingsVoiceCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default meetingsvoiceCopy;
